import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';

// World Clock cities ordered by GMT offset with London (GMT+0) in center
// West → Center (GMT+0) → East
const worldCities = [
  { name: 'Honolulu', timezone: 'Pacific/Honolulu', abbr: 'HNL', utc: '-10' },
  { name: 'San Francisco', timezone: 'America/Los_Angeles', abbr: 'SFO', utc: '-8' },
  { name: 'Denver', timezone: 'America/Denver', abbr: 'DEN', utc: '-7' },
  { name: 'Dallas', timezone: 'America/Chicago', abbr: 'DAL', utc: '-6', isHighlighted: true },
  { name: 'Boston', timezone: 'America/New_York', abbr: 'BOS', utc: '-5' },
  { name: 'London', timezone: 'Europe/London', abbr: 'LON', utc: '+0', isCenter: true },
  { name: 'Paris', timezone: 'Europe/Paris', abbr: 'PAR', utc: '+1' },
  { name: 'Moscow', timezone: 'Europe/Moscow', abbr: 'MSK', utc: '+3' },
  { name: 'Tokyo', timezone: 'Asia/Tokyo', abbr: 'TYO', utc: '+9' },
  { name: 'Beijing', timezone: 'Asia/Shanghai', abbr: 'BEI', utc: '+8' },
];

const WorldClockTicker = () => {
  const [times, setTimes] = useState({});
  const barRef = useRef(null);

  // Update clocks every second
  useEffect(() => {
    const updateTimes = () => {
      const newTimes = {};
      worldCities.forEach((city) => {
        const time = new Date().toLocaleTimeString('en-US', {
          timeZone: city.timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        newTimes[city.name] = time;
      });
      setTimes(newTimes);
    };

    updateTimes();
    const timer = setInterval(updateTimes, 1000);
    return () => clearInterval(timer);
  }, []);

  // Keep a CSS variable in sync with the actual top-bar height so the
  // sticky <Header> offset and section scroll-margin stay accurate.
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const update = () => {
      const h = el.offsetHeight || 0;
      document.documentElement.style.setProperty('--ticker-bar-height', `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={barRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-navy-900 text-white text-sm border-b border-white/10 hidden md:block"
      data-testid="world-clock-ticker"
    >
      {/* World Clocks Row */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        <div className="flex items-center justify-center py-1.5">
          <div className="flex items-center gap-1 sm:gap-2">
            {worldCities.map((city) => {
              const isHighlighted = city.isHighlighted;
              return (
                <div
                  key={city.name}
                  className={`flex flex-col items-center px-1.5 sm:px-2 py-0.5 rounded transition-all min-w-0 ${
                    isHighlighted ? 'bg-gold-500/20 border border-gold-500/50' : ''
                  }`}
                >
                  <span
                    className={`text-[9px] sm:text-[10px] leading-tight truncate max-w-[60px] sm:max-w-none ${
                      isHighlighted ? 'text-gold-400 font-semibold' : 'text-white/60'
                    }`}
                  >
                    {city.name}
                  </span>
                  <span
                    className={`font-mono text-[11px] sm:text-xs font-medium leading-tight ${
                      isHighlighted ? 'text-gold-300' : 'text-white'
                    }`}
                  >
                    {times[city.name] || '--:--'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorldClockTicker;
